import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CustomRadioComponent } from '../custom-radio/custom-radio.component';
import { CurrencyPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { IconButtonDirective } from '../../directives/icon-button.directive';
import { ReportTypes, SubscriptionPlans } from '../../../user/user/user.model';
import { ReportsService } from '../../../reports/reports.service';
import { SubscriptionData } from '../../../user/user/subscriptions-menu/subscriptions-menu.component';
import { PromoCodeLookup } from '../../../user/subscriptions.service';
import { GoogleAnalyticsService } from '../../../user/google-analytics.service';
import { UserAccessService } from '../../../user/access/user-access.service';
import { Router } from '@angular/router';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { ModalService } from '../../services/modal.service';
import { ReportData } from '../../../reports/order-report/choose-type/report-type/reports-info';

export type CardKey = ReportTypes | SubscriptionPlans;
export type CardType = 'REPORTS' | 'SUBSCRIPTION_PLANS';

@Component({
  selector: 'app-type-card',
  imports: [
    CustomRadioComponent,
    IconComponent,
    IconButtonDirective,
    NgClass,
    CurrencyPipe,
    NgIf,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgForOf,
  ],
  templateUrl: './type-card.component.html',
  styleUrls: ['./type-card.component.scss', '../../../app.component.scss'],
})
export class TypeCardComponent implements OnChanges, AfterViewInit {
  @ViewChild('controlBlock', { static: true, read: ElementRef })
  controlBlock!: ElementRef<HTMLElement>;

  @ViewChild('featuresList', { static: true, read: ElementRef })
  featuresList!: ElementRef<HTMLElement>;

  @ViewChild('additionalInfo', { static: true, read: ElementRef })
  additionalInfo!: ElementRef<HTMLElement>;

  @Input() data!: ReportData | SubscriptionData;
  @Input() readyToOrder: boolean = false;
  @Input() isSelected: boolean = false;
  @Input() isCurrentActivePlan = false;
  @Input() cardType!: CardType;
  @Input() isSidePanel: boolean = false;
  @Input() isHeightIncreased: boolean = false;

  @Input() isThisSubscriptionActive: boolean = false;
  @Input() isPeriodMonthly!: boolean;
  @Input() isSubscriptionUrlSelected!: boolean;
  @Input() promoCodeDetails: PromoCodeLookup | null = null;
  @Input() promoCode: string | null = null;
  @Input() source: 'profile' | 'paywall' = 'profile';

  @Output() selectType = new EventEmitter<CardKey>();
  @Output() featureExtended = new EventEmitter<void>();

  public isFeaturesExtended = false;

  public freeDaysFromPromoCode: number | null = null;
  public freePeriodString: string | null = null;
  public isSeeIndicatorsActive: boolean = false;

  constructor(
    public reportsService: ReportsService,
    private gaService: GoogleAnalyticsService,
    private modalService: ModalService,
    private accessService: UserAccessService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.promoCodeDetails) {
      this.handlePromoCodeDetails();
    }
  }

  ngAfterViewInit(): void {
    // Workaround to make card resize after init to fix spacing
    setTimeout(() => this.featureExtended.emit(), 100);
  }

  public handleSample(e: MouseEvent): void {
    e.stopPropagation();
    if ((this.data as ReportData).sample) {
      window.open((this.data as ReportData).sample, '_blank');
    }
  }

  public emitReportTypeChange(): void {
    this.selectType.emit(this.data.id as CardKey);
  }

  public extendFeature(): void {
    this.isFeaturesExtended = !this.isFeaturesExtended;

    this.featureExtended.emit();
  }

  public isReportData(x: ReportData | SubscriptionData): x is ReportData {
    return this.cardType === 'REPORTS';
  }

  private handlePromoCodeDetails(): void {
    if (!this.promoCodeDetails?.active) {
      this.resetPromoCodeDetails();
      return;
    }

    if (this.isPromoCodeApplicable()) {
      this.updateFreePeriodDetails();
    }
  }

  private resetPromoCodeDetails(): void {
    this.freeDaysFromPromoCode = null;
    this.freePeriodString = null;
  }

  private isPromoCodeApplicable(): boolean {
    return !!this.promoCodeDetails?.toSubscriptions.includes(
      this.data.id as SubscriptionPlans
    );
  }

  private updateFreePeriodDetails(): void {
    if(this.promoCodeDetails!.numberOfFreeDays == null) {
      return;
    }
    this.freeDaysFromPromoCode = this.promoCodeDetails!.numberOfFreeDays;

    const months = Math.floor(this.freeDaysFromPromoCode / 30);
    const years = Math.floor(months / 12);

    if (years >= 1) {
      this.freePeriodString = years === 1 ? '1 year' : `${years} years`;
    } else if (months >= 1) {
      this.freePeriodString = months === 1 ? '1 month' : `${months} months`;
    } else {
      this.freePeriodString = `${this.freeDaysFromPromoCode} days`;
    }
  }

  public toggleIndicators() {
    this.isSeeIndicatorsActive = !this.isSeeIndicatorsActive;
    if (this.isSeeIndicatorsActive) {
      this.gaService.logSeeAllIndicators(this.data.id as SubscriptionPlans);
    }
  }

  public handleSubscription(plan: SubscriptionPlans): void {
    this.gaService.logSubscriptionGetStarted(
      plan,
      this.isPeriodMonthly,
      this.promoCode
    );

    if (this.source === 'paywall') {
      this.gaService.logGetStartedPaywall(plan, this.isPeriodMonthly, this.promoCode);
    } else {
      this.gaService.logGetStartedProfile(plan, this.isPeriodMonthly, this.promoCode);
    }

    const subscriptionData = this.data as import('../../../user/user/subscriptions-menu/subscriptions-menu.component').SubscriptionData;
    if (subscriptionData.regionalPricing) {
      this.modalService.openCoverageAreaModal(
        plan,
        subscriptionData.regionalPricing,
        this.promoCode,
        this.freePeriodString
      );
      return;
    }

    if (!this.accessService.getIsAuthorized()) {
      const backlink = encodeURIComponent(
        `/user/subscription?plan=${plan}&yearly=${!this.isPeriodMonthly}`
      );
      this.modalService.closeSubscriptionsModal();
      this.router.navigateByUrl(`/sign-up/individual?backlink=${backlink}`);
      return;
    }

    this.modalService.handleSubscriptionConfirmationModal(
      plan,
      this.isPeriodMonthly,
      this.freePeriodString,
      this.promoCode
    );
  }

  public onSampleReportClick() {
    this.gaService.logSampleReportClicked(this.data.id as SubscriptionPlans);
  }

  protected readonly JSON = JSON;
}
